import { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { Input } from '@/components/ui/input';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { cn } from '@/lib/utils';

interface TeamMember {
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  onMentionsChange: (mentions: string[]) => void;
  teamMembers: TeamMember[];
  currentUserId?: string;
  placeholder?: string;
  className?: string;
  onSubmit?: () => void;
  onCancel?: () => void;
  autoFocus?: boolean;
  disabled?: boolean;
}

export function MentionInput({
  value,
  onChange,
  onMentionsChange,
  teamMembers,
  currentUserId,
  placeholder = "Escreva um comentário... Use @ para mencionar",
  className,
  onSubmit,
  onCancel,
  autoFocus = false,
  disabled = false,
}: MentionInputProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredMembers, setFilteredMembers] = useState<TeamMember[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionStartPos, setMentionStartPos] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Extract mentions from text
  const extractMentions = (text: string): string[] => {
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const mentions: string[] = [];
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      mentions.push(match[2]); // user_id
    }
    return mentions;
  };

  // Convert display text to storage format and vice versa
  const getDisplayText = (text: string): string => {
    // Convert @[Name](user_id) to @Name
    return text.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    
    // Check if we're in a mention context
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Check if there's a space after @ (which would end the mention)
      if (!textAfterAt.includes(' ') && textAfterAt.length <= 20) {
        setMentionSearch(textAfterAt.toLowerCase());
        setMentionStartPos(lastAtIndex);
        const filtered = teamMembers.filter(m => 
          m.user_id !== currentUserId && // Exclude current user from suggestions
          m.full_name?.toLowerCase().includes(textAfterAt.toLowerCase())
        );
        setFilteredMembers(filtered);
        setShowSuggestions(filtered.length > 0);
        setSelectedIndex(0);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
    
    onChange(newValue);
  };

  const insertMention = (member: TeamMember) => {
    if (mentionStartPos === null) return;
    
    const beforeMention = value.substring(0, mentionStartPos);
    const afterMention = value.substring(mentionStartPos + mentionSearch.length + 1);
    
    // Format: @[Name](user_id)
    const mentionText = `@[${member.full_name || 'Usuário'}](${member.user_id})`;
    const newValue = beforeMention + mentionText + ' ' + afterMention;
    
    onChange(newValue);
    onMentionsChange(extractMentions(newValue));
    setShowSuggestions(false);
    setMentionStartPos(null);
    
    // Focus back on input
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredMembers.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredMembers.length - 1
        );
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (filteredMembers[selectedIndex]) {
          insertMention(filteredMembers[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    } else {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onMentionsChange(extractMentions(value));
        onSubmit?.();
      } else if (e.key === 'Escape') {
        onCancel?.();
      }
    }
  };

  // Update mentions when value changes externally
  useEffect(() => {
    onMentionsChange(extractMentions(value));
  }, []);

  return (
    <div className={cn("relative", className)}>
      <Input
        ref={inputRef}
        value={getDisplayText(value)}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="h-10 text-base"
        autoFocus={autoFocus}
        disabled={disabled}
        onBlur={() => {
          // Delay hiding to allow click on suggestion
          setTimeout(() => setShowSuggestions(false), 200);
        }}
      />
      
      {/* Suggestions dropdown */}
      {showSuggestions && filteredMembers.length > 0 && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-popover border rounded-md shadow-lg z-50 max-h-48 overflow-y-auto">
          {filteredMembers.map((member, index) => (
            <button
              key={member.user_id}
              type="button"
              className={cn(
                "w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-accent transition-colors",
                index === selectedIndex && "bg-accent"
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention(member);
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <SignedAvatar
                src={member.avatar_url}
                bucket="avatars"
                className="w-6 h-6"
                fallback={
                  <span className="bg-muted text-xs w-full h-full flex items-center justify-center rounded-full">
                    {member.full_name?.charAt(0) || '?'}
                  </span>
                }
              />
              <span className="text-sm font-medium truncate">
                {member.full_name || 'Usuário'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Helper to render comment text with highlighted mentions
export function renderCommentWithMentions(
  content: string,
  currentUserId?: string
): React.ReactNode {
  // Split by mention pattern @[Name](user_id)
  const parts = content.split(/(@\[[^\]]+\]\([^)]+\))/g);
  
  return parts.map((part, index) => {
    const mentionMatch = part.match(/@\[([^\]]+)\]\(([^)]+)\)/);
    if (mentionMatch) {
      const [, name, userId] = mentionMatch;
      const isCurrentUser = userId === currentUserId;
      return (
        <span
          key={index}
          className={cn(
            "font-medium text-primary",
            isCurrentUser && "bg-primary/20 rounded px-0.5"
          )}
        >
          @{name}
        </span>
      );
    }
    return <span key={index}>{part}</span>;
  });
}